import { useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useEffect } from 'react'
import { useAnalytics } from '~/hooks/useAnalytics'
import { ApplicationRoutes } from '~/hooks/useRouting'
import emitterService from '~/services/EventEmitter/EmitterService'
import { AuthenticationEmitterEventType } from '~/services/EventEmitter/types'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
} from '~/types/Analytics'
import {
  AuthenticationState,
  AuthenticationUpdateType,
} from '~/types/AuthenticationTypes'

import {
  AppSessionJwts as AppSessionJwtData,
  SessionTokenData,
  ValidationTokenData,
} from '@mangovoice/types'

import { StorageKeys } from './StorageKeys'

export interface UseAuthenticationStateResponse {
  authenticationState: AuthenticationState
  jwtData?: AppSessionJwtData
  validationTokenData?: ValidationTokenData
  sessionData?: SessionTokenData
  isUserLoggedIn: boolean
  clearAllAuthenticationData: () => void
  logout: () => void
}

const localAuthStore = localStorage.getItem('authData')
let localAuthState: AuthenticationState = localAuthStore
  ? JSON.parse(localAuthStore)
  : {}

const broadcastLocalAuthStateUpdated = (
  updateType: AuthenticationUpdateType,
) => {
  emitterService.emit(AuthenticationEmitterEventType.USER_AUTH_DATA_UPDATED, {
    newAuthState: localAuthState,
    updateType,
  })
}

export const getCurrentAuthState = (): AuthenticationState => ({
  ...localAuthState,
})

let _setValidationTokenHook:
  | ((newValidationToken?: ValidationTokenData) => void)
  | undefined

export const setValidationToken = (
  newValidationToken?: ValidationTokenData,
) => {
  localAuthState = {
    ...localAuthState,
    validationToken: newValidationToken,
  }
  _setValidationTokenHook?.(newValidationToken)
  broadcastLocalAuthStateUpdated(AuthenticationUpdateType.ValidationToken)
}
let _setSessionHook: ((newSession?: SessionTokenData) => void) | undefined

export const setSession = (newSession?: SessionTokenData) => {
  localAuthState = {
    ...localAuthState,
    session: newSession,
  }
  _setSessionHook?.(newSession)
  broadcastLocalAuthStateUpdated(AuthenticationUpdateType.Session)
}
let _setAppSessionJwtsHook:
  | ((newAppSessionJwts?: AppSessionJwtData) => void)
  | undefined

export const setAppSessionJwts = (newAppSessionJwts?: AppSessionJwtData) => {
  localAuthState = {
    ...localAuthState,
    appSessionJwts: newAppSessionJwts,
  }
  _setAppSessionJwtsHook?.(newAppSessionJwts)
  broadcastLocalAuthStateUpdated(AuthenticationUpdateType.AppSessionJwt)
}

const authenticationStateAtom = atomWithStorage<AuthenticationState>(
  StorageKeys.AuthData,
  {},
)

export const getAuthStateFromLocalStorage = () => {
  const _localAuthStore = localStorage.getItem('authData')
  const _localAuthState: AuthenticationState = _localAuthStore
    ? JSON.parse(_localAuthStore)
    : {}
  return _localAuthState
}
export const useAuthenticationState = (): UseAuthenticationStateResponse => {
  const [authenticationState, setAuthenticationState] = useAtom(
    authenticationStateAtom,
  )
  const queryClient = useQueryClient()
  const { track } = useAnalytics(AnalyticsNamespace.AUTHENTICATION)

  const isUserLoggedIn = Boolean(
    authenticationState.appSessionJwts?.access_token,
  )

  useEffect(() => {
    setAuthenticationState(localAuthState)
  }, [])

  _setValidationTokenHook = (newValidationToken?: ValidationTokenData) => {
    setAuthenticationState({
      ...localAuthState,
      validationToken: newValidationToken,
    })
  }

  _setAppSessionJwtsHook = (newAppSessionJwts?: AppSessionJwtData) => {
    setAuthenticationState({
      ...localAuthState,
      appSessionJwts: newAppSessionJwts,
    })
  }

  _setSessionHook = (newSessionData?: SessionTokenData) => {
    setAuthenticationState({
      ...localAuthState,
      session: newSessionData,
    })
  }

  const clearAllAuthenticationData = () => {
    localAuthState = {}
    setAuthenticationState(localAuthState)
  }

  const logout = () => {
    if (!isUserLoggedIn) {
      return
    }

    clearAllAuthenticationData()

    //allow components to respond to logout
    emitterService.emit(
      AuthenticationEmitterEventType.USER_LOGGED_OUT,
      undefined,
    )

    //track logout event
    track(AuthenticationAnalyticsEvents.LogoutSuccess)

    //clear all query caches in the query client
    queryClient.clear()

    //wipe authentication state local storage
    localStorage.removeItem('authData')

    //navigate to login page
    window.location.href = ApplicationRoutes.LOGIN
  }

  return {
    authenticationState,
    jwtData: authenticationState.appSessionJwts,
    validationTokenData: authenticationState.validationToken,
    sessionData: authenticationState.session,
    isUserLoggedIn,
    clearAllAuthenticationData,
    logout,
  }
}
